/**
 * @fileoverview
 * Time Picker Component - Using timepicker-ui library with clock face interface
 * Compatible with Firefox, Chrome, Safari, Edge
 *
 * @version 2.0.0
 * @author KATALYST TEAM
 */

import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, forwardRef, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, OnChanges, SimpleChanges, AfterViewInit, OnDestroy } from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import TimepickerUI from 'timepicker-ui';
// timepicker-ui/main.css is included via angular.json styles to avoid Vite optimizeDeps error

@Component({
  selector: 'app-time-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './time-picker.component.html',
  styleUrl: './time-picker.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimePickerComponent),
      multi: true,
    },
  ],
})
export class TimePickerComponent implements ControlValueAccessor, OnChanges, AfterViewInit, OnDestroy {
  @Input() label: string = '';
  @Input() placeholder: string = '-- : --';
  @Input() showAsterisk: boolean = false;
  @Input() disabled: boolean = false;
  @Input() errorMessage: string | null = null;
  @Input() showError: boolean = false;
  @Input() min: string = '';
  @Input() max: string = '';
  /** When set, if this date is today, user cannot select a time later than current time. */
  @Input() selectedDate: Date | string | null = null;
  @Input() format: '12H' | '24H' = '24H'; // Format option: '12H' or '24H', default is '24H'
  @Output() timeChange = new EventEmitter<string>();
  
  @ViewChild('timeInput', { static: false }) timeInput!: ElementRef<HTMLInputElement>;
  
  private timepickerInstance: TimepickerUI | null = null;
  selectedTime: string = ''; // Stored in 24-hour format (HH:MM)
  displayValue: string = ''; // Display value based on format input
  currentTime: string = '';
  internalErrorMessage: string = ''; // Internal validation error message

  onChange = (value: string) => {};
  onTouched = () => {};

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    this.currentTime = this.getCurrentTime();
  }

  ngAfterViewInit(): void {
    // Wait for next tick to ensure view is ready
    setTimeout(() => {
      this.initializeTimepicker();
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.timepickerInstance) {
      this.timepickerInstance.destroy();
      this.timepickerInstance = null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['format'] && this.timeInput?.nativeElement && this.timepickerInstance) {
      // Reinitialize with new format
      this.initializeTimepicker();
    }
    if (changes['disabled'] && this.timeInput?.nativeElement) {
      this.timeInput.nativeElement.disabled = this.disabled;
    }
    if (changes['min']) {
      // Reinitialize to update min time constraint
      if (this.timeInput?.nativeElement && this.timepickerInstance) {
        this.initializeTimepicker();
      }
    }
    if (changes['selectedDate']) {
      // Reinitialize to update max time constraint when selected date is today
      if (this.timeInput?.nativeElement && this.timepickerInstance) {
        this.initializeTimepicker();
      }
    }
  }

  /**
   * Returns true if selectedDate is the current calendar day (today).
   */
  private isSelectedDateToday(): boolean {
    if (this.selectedDate == null) return false;
    const d = typeof this.selectedDate === 'string' ? new Date(this.selectedDate) : this.selectedDate;
    if (isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() &&
           d.getMonth() === now.getMonth() &&
           d.getDate() === now.getDate();
  }

  /**
   * Build disabledTime config for times after current time (used when selected date is today).
   * Returns interval in 12-hour format as expected by timepicker-ui.
   */
  private getDisabledTimeAfterNow(): { interval: string[] } | undefined {
    if (!this.isSelectedDateToday()) return undefined;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    // Disable from (current time + 1 minute) to end of day
    let fromMinute = currentMinute + 1;
    let fromHour = currentHour;
    if (fromMinute >= 60) {
      fromMinute = 0;
      fromHour += 1;
    }
    if (fromHour >= 24) return undefined; // no future time left today
    const from24 = `${String(fromHour).padStart(2, '0')}:${String(fromMinute).padStart(2, '0')}`;
    const from12 = this.format24To12(from24);
    return { interval: [`${from12} - 11:59 PM`] };
  }

  /** Format HH:MM (24h) to "H:MM AM/PM" for timepicker-ui interval. */
  private format24To12(time24: string): string {
    const [h, m] = time24.split(':').map(Number);
    if (h === 0) return `12:${String(m).padStart(2, '0')} AM`;
    if (h === 12) return `12:${String(m).padStart(2, '0')} PM`;
    if (h < 12) return `${h}:${String(m).padStart(2, '0')} AM`;
    return `${h - 12}:${String(m).padStart(2, '0')} PM`;
  }

  /**
   * Initialize timepicker-ui library
   */
  private initializeTimepicker(): void {
    if (!this.timeInput?.nativeElement) {
      return;
    }

    // Destroy existing instance if any
    if (this.timepickerInstance) {
      this.timepickerInstance.destroy();
      this.timepickerInstance = null;
    }

    const disabledTime = this.getDisabledTimeAfterNow();
    const clockConfig: Record<string, unknown> = {
      type: this.format === '12H' ? '12h' : '24h',
    };
    if (disabledTime) {
      (clockConfig as any).disabledTime = disabledTime;
    }

    // Configure timepicker-ui with clock face interface
    this.timepickerInstance = new TimepickerUI(this.timeInput.nativeElement, {
      clock: clockConfig,
      callbacks: {
        onConfirm: (data: any) => {
          // data contains hour, minutes, and type (AM/PM)
          const hour = data.hour || '00';
          const minute = data.minutes || '00';
          let time24 = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
          
          // Convert to 24-hour format if 12H format
          if (this.format === '12H' && data.type) {
            const hourNum = parseInt(hour, 10);
            let hour24 = hourNum;
            if (data.type === 'PM' && hourNum !== 12) {
              hour24 = hourNum + 12;
            } else if (data.type === 'AM' && hourNum === 12) {
              hour24 = 0;
            }
            time24 = `${String(hour24).padStart(2, '0')}:${minute.padStart(2, '0')}`;
          }
          
          this.handleTimeSelection(time24);
        },
        onCancel: () => {
          // Handle cancel if needed
        },
      },
    } as any);

    // Set initial value - empty by default, only use selectedTime if it exists
    const initialTimeValue = this.selectedTime || '';
    this.timeInput.nativeElement.value = initialTimeValue;
    
    // Set disabled state
    this.timeInput.nativeElement.disabled = this.disabled;
    
    // Create the timepicker instance (this will attach the clock interface)
    this.timepickerInstance.create();
    
    // Only set value if there's a selected time, otherwise leave it empty
    if (this.selectedTime) {
      this.timepickerInstance.setValue(this.selectedTime, true);
    }
  }

  /**
   * Open the timepicker popup
   */
  openTimepicker(): void {
    if (this.timepickerInstance && !this.disabled) {
      this.timepickerInstance.open();
    }
  }

  /**
   * Handle time selection from timepicker-ui
   */
  private handleTimeSelection(time: string): void {
    // Clear any internal error
    this.internalErrorMessage = '';
    this.showError = false;

    // When selected date is today, do not allow time beyond current time
    if (this.isSelectedDateToday()) {
      const now = this.getCurrentTime();
      if (this.compareTime(time, now) > 0) {
        this.internalErrorMessage = 'Time cannot exceed current time for today.';
        this.showError = true;
        this.changeDetectorRef.markForCheck();
        return;
      }
    }

    this.selectedTime = time;
    this.updateDisplayValue();
    this.onChange(time);
    this.timeChange.emit(time);
    this.onTouched();
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Get current time in HH:MM format (24-hour)
   */
  private getCurrentTime(): string {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  /**
   * Compare two times in HH:MM format
   */
  private compareTime(time1: string, time2: string): number {
    const [h1, m1] = time1.split(':').map(Number);
    const [h2, m2] = time2.split(':').map(Number);
    
    if (h1 !== h2) {
      return h1 - h2;
    }
    return m1 - m2;
  }

  /**
   * Convert 12-hour format to 24-hour format
   */
  private convertTo24Hour(time12: string): string {
    // Format: "HH:MM AM" or "HH:MM PM"
    const match = time12.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (!match) {
      // Try to extract just HH:MM
      const timeMatch = time12.match(/(\d{1,2}):(\d{2})/);
      if (timeMatch) {
        return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}`;
      }
      return time12;
    }

    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();

    if (period === 'AM') {
      if (hours === 12) hours = 0;
    } else {
      // PM
      if (hours !== 12) hours += 12;
    }

    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  /**
   * Update display value based on format input
   * Always adds AM/PM indicator for both 12H and 24H formats
   */
  private updateDisplayValue(): void {
    if (!this.selectedTime) {
      this.displayValue = '';
      return;
    }

    const [hours, minutes] = this.selectedTime.split(':').map(Number);
    
    // Determine AM/PM based on hour (0-11 = AM, 12-23 = PM)
    const period = hours >= 12 ? 'PM' : 'AM';

    if (this.format === '12H') {
      // Show 12-hour format with AM/PM
      // Convert 24-hour to 12-hour: 0->12, 1-11->1-11, 12->12, 13-23->1-11
      let hour12: number;
      if (hours === 0) {
        hour12 = 12; // Midnight (00:xx) becomes 12:xx AM
      } else if (hours === 12) {
        hour12 = 12; // Noon (12:xx) stays 12:xx PM
      } else if (hours > 12) {
        hour12 = hours - 12; // Afternoon (13-23) becomes 1-11 PM
      } else {
        hour12 = hours; // Morning (1-11) stays 1-11 AM
      }
      this.displayValue = `${String(hour12).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
    } else {
      // Show 24-hour format with AM/PM indicator
      this.displayValue = `${this.selectedTime} ${period}`;
    }
  }

  /**
   * Format time for display (extract HH:MM from various formats)
   */
  private formatTimeForDisplay(time: string): string {
    if (!time) return '';
    const match = time.match(/(\d{1,2}):(\d{2})/);
    if (match) {
      return `${match[1].padStart(2, '0')}:${match[2]}`;
    }
    return time;
  }

  /**
   * ControlValueAccessor: Write value from form
   */
  writeValue(value: string): void {
    if (value) {
      // Handle both 12-hour and 24-hour input
      let formattedTime = value;
      
      // Check if value contains AM/PM (12-hour format)
      if (value.includes('AM') || value.includes('PM')) {
        formattedTime = this.convertTo24Hour(value);
      } else {
        // Format value to HH:MM (24-hour format)
        formattedTime = this.formatTimeForDisplay(value);
      }
      
      // Clear any internal error
      this.internalErrorMessage = '';
      this.showError = false;
      
      this.selectedTime = formattedTime;
      this.updateDisplayValue();
      
      // Update timepicker-ui if initialized
      if (this.timepickerInstance) {
        this.timepickerInstance.setValue(formattedTime, true);
      } else if (this.timeInput?.nativeElement) {
        this.timeInput.nativeElement.value = formattedTime;
      }
    } else {
      this.selectedTime = '';
      this.displayValue = '';
      if (this.timepickerInstance) {
        // Don't set any value - keep it empty
        this.timepickerInstance.setValue('', false);
      } else if (this.timeInput?.nativeElement) {
        this.timeInput.nativeElement.value = '';
      }
    }
    this.changeDetectorRef.markForCheck();
  }

  /**
   * ControlValueAccessor: Register onChange callback
   */
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  /**
   * ControlValueAccessor: Register onTouched callback
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (this.timeInput?.nativeElement) {
      this.timeInput.nativeElement.disabled = isDisabled;
    }
    this.changeDetectorRef.markForCheck();
  }
}
